import { MacroCategoryIds, Targeting } from '@sbt-web/adv';
import { Category, getMacroOfCategoryID } from '@sbt-web/category-utils';
import { AdTypes, GeoEntry } from '@sbt-web/network/types';
import { RadiusSearch } from '@/reducers/search';

export interface ListingTargetingProps {
  category: Category;
  adType: AdTypes;
  pageIndex: number;
  includeShippableOnly: boolean;
  query?: string;
  filtersLabels?: Record<string, string>;
  geo?: GeoEntry;
  radiusSearch?: RadiusSearch;
  isPro?: boolean;
  userActiveAd?: boolean;
}

const filtersMapping: Record<string, string> = {
  brand: 'cb',
  model: 'cm',
  fuel: 'fl',
  minRegDate: 'ys',
  maxRegDate: 'ye',
  minMileage: 'ms',
  maxMileage: 'me',
  carType: 'ct',
  gearbox: 'gr',
  emission: 'pl',
  vehicleStatus: 'cvs',
  qualitySeal: 'qltsf',
  priceFrom: 'ps',
  priceTo: 'pe',
  roomMin: 'rs', // Real estate
  roomMax: 're', // Real estate
  jobCategory: 'jc',
  contractType: 'cnt',
  workLevel: 'wl',
  degree: 'd',
  advertiserType: 'advt',
};

const typeFiltersMapping: Record<string, string> = {
  contractType: 'cnt',
  computerType: 'pt',
  audioVideoType: 'avt',
  phoneType: 'pht',
  bicycleType: 'bkt',
  sportType: 'st',
  bookType: 'bot',
  clothingType: 'clt',
  roomType: 'rt',
  motorcycleType: 'mt',
  caravanType: 'crt',
  carType: 'ct',
  shipType: 'bt',
  commercialVehicleType: 'cvt',
  serviceType: 'set',
  hobbyType: 'ht',
  officeType: 'ot',
};

const buildType = (
  filtersLabels: Record<string, string | undefined> | undefined = {}
): string | undefined => {
  const key = Object.values(typeFiltersMapping).find(
    (key) => filtersLabels?.[key]
  );
  return key ? filtersLabels?.[key] : undefined;
};

const setProTargeting = (
  targeting: Targeting,
  advertiserType: string | undefined
): void => {
  if (!advertiserType) {
    return;
  }
  const isPro = advertiserType.toLowerCase() === 'azienda';
  targeting.advertiserType = isPro ? 'pro' : 'freemium';
};

export const targetingForListing = ({
  radiusSearch,
  geo,
  query,
  category,
  adType,
  filtersLabels,
  pageIndex,
  includeShippableOnly,
  isPro,
  userActiveAd,
}: ListingTargetingProps): Targeting => {
  const targeting: Targeting = {};

  // Category:
  const macro = getMacroOfCategoryID(category.id);
  targeting.macroCategoryId = macro.id as MacroCategoryIds;
  targeting.parentCategoryName = macro.label;
  targeting.categoryName = category.label;

  // Type:
  targeting.adType = adType;
  const type = buildType(filtersLabels);
  if (type) {
    targeting.type = type;
  }

  //User:
  targeting.userType = isPro ? 'prouser' : 'freeuser';
  targeting.userActiveAd = userActiveAd ? 'yes' : 'no';

  //Advertiser type:
  setProTargeting(targeting, filtersLabels?.[filtersMapping.advertiserType]);

  //Geo:
  const region = radiusSearch?.addressToTrack?.state ?? geo?.region?.value;
  if (region) {
    targeting.region = region;
  }
  const province = radiusSearch?.addressToTrack?.county ?? geo?.city?.value;
  if (province) {
    targeting.province = province;
  }
  const town = radiusSearch?.addressToTrack?.city ?? geo?.town?.value;
  if (town) {
    targeting.town = town;
  }

  // Query:
  const queryString = query ?? category.label;
  if (queryString) {
    targeting.queryString = queryString;
  }
  targeting.queryIsEmpty = query === undefined;

  targeting.itemShippable = includeShippableOnly ?? false;

  //Price:
  if (filtersLabels?.[filtersMapping.priceFrom]) {
    targeting.priceFrom = filtersLabels[filtersMapping.priceFrom].replace(
      /\./g,
      ''
    );
  }
  if (filtersLabels?.[filtersMapping.priceTo]) {
    targeting.priceTo = filtersLabels[filtersMapping.priceTo].replace(
      /\./g,
      ''
    );
  }

  //Page number:
  if (pageIndex) {
    targeting.searchPageNumber = pageIndex;
  }

  //Automotive:
  if (filtersLabels?.[filtersMapping.brand]) {
    targeting.brand = filtersLabels[filtersMapping.brand];
  }
  if (filtersLabels?.[filtersMapping.model]) {
    targeting.model = filtersLabels[filtersMapping.model];
  }
  if (filtersLabels?.[filtersMapping.fuel]) {
    targeting.fuel = filtersLabels[filtersMapping.fuel];
  }
  if (filtersLabels?.[filtersMapping.minRegDate]) {
    targeting.minRegDate = filtersLabels[filtersMapping.minRegDate];
  }
  if (filtersLabels?.[filtersMapping.maxRegDate]) {
    targeting.maxRegDate = filtersLabels[filtersMapping.maxRegDate];
  }
  if (filtersLabels?.[filtersMapping.minMileage]) {
    targeting.minMileage = filtersLabels[filtersMapping.minMileage].replace(
      /\./g,
      ''
    );
  }
  if (filtersLabels?.[filtersMapping.maxMileage]) {
    targeting.maxMileage = filtersLabels[filtersMapping.maxMileage].replace(
      /\./g,
      ''
    );
  }
  if (filtersLabels?.[filtersMapping.carType]) {
    targeting.carType = filtersLabels[filtersMapping.carType];
  }
  if (filtersLabels?.[filtersMapping.gearbox]) {
    targeting.gearbox = filtersLabels[filtersMapping.gearbox];
  }
  if (filtersLabels?.[filtersMapping.emission]) {
    targeting.emission = filtersLabels[filtersMapping.emission];
  }
  if (filtersLabels?.[filtersMapping.vehicleStatus]) {
    targeting.vehicleStatus = filtersLabels[filtersMapping.vehicleStatus];
  }
  if (filtersLabels?.[filtersMapping.qualitySeal]) {
    targeting.qualitySeal = filtersLabels[filtersMapping.qualitySeal];
  }

  // Real estate:
  if (filtersLabels?.[filtersMapping.roomMin]) {
    targeting.room = filtersLabels[filtersMapping.roomMin];
  } else if (filtersLabels?.roomMax) {
    targeting.room = filtersLabels.roomMax;
  }

  // Jobs:
  if (filtersLabels?.[filtersMapping.jobCategory]) {
    targeting.jobCategory = filtersLabels[filtersMapping.jobCategory];
  }
  if (filtersLabels?.[filtersMapping.contractType]) {
    targeting.contractType = filtersLabels[filtersMapping.contractType];
  }
  if (filtersLabels?.[filtersMapping.workLevel]) {
    targeting.workLevel = filtersLabels[filtersMapping.workLevel];
  }
  if (filtersLabels?.[filtersMapping.degree]) {
    targeting.degree = filtersLabels[filtersMapping.degree];
  }

  return targeting;
};
